import React from 'react';
import { MEDIA_VARIANTS } from '../../../media';

function field(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

function habitatSummary(enemy) {
  const habitat = field(enemy, 'roamingHabitat', 'RoamingHabitat');
  if (!habitat) return 'Any walkable tile';
  const bases = field(habitat, 'allowedBaseKeys', 'AllowedBaseKeys') || [];
  const details = field(
    habitat,
    'allowedDetailKeysAny',
    'AllowedDetailKeysAny',
  ) || [];
  const near = field(habitat, 'near', 'Near');
  const parts = [];
  if (bases.length) parts.push(`base: ${bases.join(', ')}`);
  if (details.length) parts.push(`detail: ${details.join(', ')}`);
  if (near) {
    const nearKeys = [
      ...(field(near, 'baseKeys', 'BaseKeys') || []),
      ...(field(near, 'detailKeysAny', 'DetailKeysAny') || []),
    ];
    parts.push(
      `near ${nearKeys.join(', ')} within ${field(
        near,
        'maxManhattanDistance',
        'MaxManhattanDistance',
      ) || 1}`,
    );
  }
  return parts.join(' · ') || 'Any walkable tile';
}

export default function EnemySpawnPreviewEditor({
  enemies = [],
  error = '',
  fixedEncounter = null,
  fixedEncounterEnemies = [],
  fixedEncounterValidation = '',
  getImageUrl,
  mapHeight = 1,
  mapWidth = 1,
  onAddFixedEncounter,
  onChangeFixedEncounter,
  onPlaceFixedEncounter,
  onPreview,
  onRemoveFixedEncounter,
  onSelectEnemy,
  placingFixedEncounter = false,
  preview = null,
  previewBusy = false,
  selectedEnemyId = '',
}) {
  const previewDefinitions = field(preview, 'enemies', 'Enemies') || [];
  const previewByEnemyId = new Map(previewDefinitions.map(definition => [
    field(definition, 'enemyId', 'EnemyId'),
    definition,
  ]));
  const errors = field(preview, 'errors', 'Errors') || [];
  const placements = field(preview, 'placements', 'Placements') || [];
  const actorSlotCount = field(preview, 'actorSlotCount', 'ActorSlotCount');
  const ordinaryLevelCounts = placements
    .filter(placement => !field(placement, 'isChampion', 'IsChampion'))
    .reduce((counts, placement) => {
      const level = Number(field(placement, 'level', 'Level')) || 1;
      counts.set(level, (counts.get(level) || 0) + 1);
      return counts;
    }, new Map());

  return (
    <section className="world-editor-section world-editor-enemy-preview">
      <div className="world-editor-section__heading">
        <div>
          <h3>Enemy preview</h3>
          <p>
            Simulate the region&apos;s runtime actor count and weighted spawn
            distribution. Select an enemy to inspect its possible spawn area
            and the selected actor&apos;s reachable patrol area.
          </p>
        </div>
      </div>
      <button
        className="button-secondary"
        disabled={previewBusy || enemies.length === 0}
        onClick={onPreview}
        type="button"
      >
        {previewBusy
          ? 'Calculating enemy spawns…'
          : preview ? 'Reroll Enemy Spawns' : 'Visualize Enemy Spawns'}
      </button>
      {error ? <p className="world-editor-feedback world-editor-feedback--error" role="alert">{error}</p> : null}
      {errors.map((issue, index) => (
        <p
          className="world-editor-feedback world-editor-feedback--error"
          key={`${field(issue, 'code', 'Code') || 'error'}-${index}`}
          role="alert"
        >
          {field(issue, 'message', 'Message') || 'Enemy preview failed.'}
        </p>
      ))}
      {preview && errors.length === 0 ? (
        <div className="world-editor-enemy-preview__summary" role="status">
          <p>
            {placements.length} of {actorSlotCount ?? placements.length} actor
            slots placed. Click the button again for another weighted sample.
          </p>
          {ordinaryLevelCounts.size ? (
            <p>
              Sample levels: {[...ordinaryLevelCounts.entries()]
                .sort(([left], [right]) => left - right)
                .map(([level, count]) => `Level ${level}: ${count}`)
                .join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}
      <div aria-label="Region enemy roster" className="world-editor-enemy-list" role="list">
        {enemies.map((enemy) => {
          const enemyId = field(enemy, 'id', 'Id');
          const name = field(enemy, 'name', 'Name') || enemyId;
          const imageId = field(enemy, 'imageId', 'ImageId');
          const previewDefinition = previewByEnemyId.get(enemyId);
          const spawnCount = (field(
            previewDefinition,
            'spawnTileIndexes',
            'SpawnTileIndexes',
          ) || []).length;
          const previewCount = field(
            previewDefinition,
            'previewCount',
            'PreviewCount',
          ) || 0;
          const imageUrl = imageId
            ? getImageUrl?.(imageId, MEDIA_VARIANTS.ENTITY_ICON)
            : null;
          return (
            <div key={enemyId} role="listitem">
              <button
                aria-pressed={selectedEnemyId === enemyId}
                className={[
                  'world-editor-enemy-card',
                  selectedEnemyId === enemyId ? 'is-selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelectEnemy(enemyId)}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className="world-editor-enemy-card__image"
                  style={{
                    backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                  }}
                >
                  {imageUrl ? null : 'E'}
                </span>
                <span className="world-editor-enemy-card__body">
                  <strong>{name}</strong>
                  <small>
                    {enemy.isChampion
                      ? 'Fixed encounter anchor'
                      : `Spawn weight ${enemy.spawnWeight}`}
                    {' · '}radius {field(
                      enemy,
                      'roamingRadiusTiles',
                      'RoamingRadiusTiles',
                    ) || 'whole region'}
                  </small>
                  <small>{habitatSummary(enemy)}</small>
                  {preview ? (
                    <small>
                      {previewCount} preview actor{previewCount === 1 ? '' : 's'}
                      {' · '}{spawnCount} possible spawn tile{spawnCount === 1 ? '' : 's'}
                    </small>
                  ) : null}
                </span>
              </button>
            </div>
          );
        })}
        {enemies.length === 0 ? (
          <p className="world-editor-empty-state">
            No positive-weight enemies or fixed encounter are assigned to this region.
          </p>
        ) : null}
      </div>

      <section className="world-editor-section" aria-labelledby="fixed-encounter-title">
        <div className="world-editor-section__heading">
          <div>
            <h4 id="fixed-encounter-title">Fixed encounter</h4>
            <p>
              Place one regional Champion or Boss in addition to the roaming
              enemy population.
            </p>
          </div>
          {!fixedEncounter ? (
            <button
              className="button-secondary"
              disabled={fixedEncounterEnemies.length === 0}
              onClick={onAddFixedEncounter}
              type="button"
            >
              Add Encounter
            </button>
          ) : null}
        </div>
        {!fixedEncounter && fixedEncounterEnemies.length === 0 ? (
          <p className="world-editor-empty-state">
            Assign a Champion or Boss enemy to this region first.
          </p>
        ) : null}
        {fixedEncounter ? (
          <div className="world-editor-card">
            <div className="world-editor-card__body">
            <label>
              Enemy
              <select
                aria-label="Fixed encounter enemy"
                value={fixedEncounter.enemyId}
                onChange={event => onChangeFixedEncounter({
                  enemyId: event.target.value,
                })}
              >
                {fixedEncounterEnemies.map(enemy => (
                  <option key={enemy.id} value={enemy.id}>
                    {enemy.name} ({enemy.typeName})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Placement ID
              <input
                aria-label="Fixed encounter placement ID"
                type="text"
                value={fixedEncounter.placementId}
                onChange={event => onChangeFixedEncounter({
                  placementId: event.target.value,
                })}
              />
            </label>
            <div className="world-editor-inline-coordinates">
              <label>
                <span>X</span>
                <input
                  aria-label="Fixed encounter X"
                  max={Math.max(0, mapWidth - 1)}
                  min="0"
                  type="number"
                  value={fixedEncounter.x}
                  onChange={event => onChangeFixedEncounter({
                    x: Math.min(
                      Math.max(0, mapWidth - 1),
                      Math.max(0, Number(event.target.value) || 0),
                    ),
                  })}
                />
              </label>
              <label>
                <span>Y</span>
                <input
                  aria-label="Fixed encounter Y"
                  max={Math.max(0, mapHeight - 1)}
                  min="0"
                  type="number"
                  value={fixedEncounter.y}
                  onChange={event => onChangeFixedEncounter({
                    y: Math.min(
                      Math.max(0, mapHeight - 1),
                      Math.max(0, Number(event.target.value) || 0),
                    ),
                  })}
                />
              </label>
            </div>
            <label className="world-editor-checkbox">
              <input
                checked={fixedEncounter.repeatAfterCooldown}
                type="checkbox"
                onChange={event => onChangeFixedEncounter({
                  repeatAfterCooldown: event.target.checked,
                  runUnlockKey: event.target.checked
                    ? ''
                    : fixedEncounter.runUnlockKey,
                })}
              />
              <span>Repeat after cooldown</span>
            </label>
            <label>
              Reward cooldown (seconds)
              <input
                aria-label="Fixed encounter cooldown seconds"
                min={fixedEncounter.repeatAfterCooldown ? 1 : 0}
                type="number"
                value={fixedEncounter.rewardCooldownSeconds}
                onChange={event => onChangeFixedEncounter({
                  rewardCooldownSeconds: Math.max(
                    0,
                    Number(event.target.value) || 0,
                  ),
                })}
              />
            </label>
            <label>
              Run unlock key
              <input
                aria-label="Fixed encounter run unlock key"
                disabled={fixedEncounter.repeatAfterCooldown}
                type="text"
                value={fixedEncounter.runUnlockKey}
                onChange={event => onChangeFixedEncounter({
                  runUnlockKey: event.target.value,
                })}
              />
            </label>
            <label>
              Guaranteed drops
              <input
                aria-label="Fixed encounter guaranteed drops"
                min="0"
                type="number"
                value={fixedEncounter.guaranteedDropCount}
                onChange={event => onChangeFixedEncounter({
                  guaranteedDropCount: Math.max(
                    0,
                    Number(event.target.value) || 0,
                  ),
                })}
              />
            </label>
            <label>
              Bonus drop chance (%)
              <input
                aria-label="Fixed encounter bonus drop chance"
                max="100"
                min="0"
                type="number"
                value={fixedEncounter.bonusDropChancePercent}
                onChange={event => onChangeFixedEncounter({
                  bonusDropChancePercent: Math.min(
                    100,
                    Math.max(0, Number(event.target.value) || 0),
                  ),
                })}
              />
            </label>
            {fixedEncounterValidation ? (
              <p className="world-editor-feedback world-editor-feedback--error" role="alert">
                {fixedEncounterValidation}
              </p>
            ) : null}
            <div className="world-editor-card__actions">
              <button
                className={`button-secondary ${placingFixedEncounter ? 'is-active' : ''}`}
                onClick={onPlaceFixedEncounter}
                type="button"
              >
                {placingFixedEncounter ? 'Click a tile…' : 'Place on Map'}
              </button>
              <button
                className="button-secondary"
                onClick={onRemoveFixedEncounter}
                type="button"
              >
                Remove
              </button>
            </div>
            </div>
          </div>
        ) : null}
      </section>
    </section>
  );
}
